import { FC, useState } from 'react';

import {
    CalendarDays,
    ChevronDown,
    MessageSquareText,
    PackageCheck,
    ScrollText,
    ShieldCheck,
} from 'lucide-react';

import { Badge } from '../../../components/ui/Badge';
import {
    getOrderReviewComplaintLabel,
    getOrderReviewRatingLabel,
} from '../../../models/orderReview';
import { OrderReview } from '../../../types';
import { cn, formatDate } from '../../../utils';

interface Props {
    review: OrderReview;
    title: string;
}

const ratingBadgeVariant = (
    rating: OrderReview['overall_rating'],
): 'success' | 'info' | 'neutral' | 'warning' | 'destructive' | 'outline' => {
    switch (rating) {
        case 'very_good':
            return 'success';
        case 'good':
            return 'info';
        case 'neutral':
            return 'neutral';
        case 'bad':
            return 'warning';
        case 'very_bad':
            return 'destructive';
        default:
            return 'outline';
    }
};

export const OrderReviewSummary: FC<Props> = ({ review, title }) => {
    const [expanded, setExpanded] = useState(false);
    const comment = review.comment?.trim();
    const hasPackagingRating = Boolean(review.packaging_rating);
    const complaintLabels = review.complaints
        .map(getOrderReviewComplaintLabel)
        .filter((label): label is string => Boolean(label));
    const collapsedSummary = [
        `Загалом: ${getOrderReviewRatingLabel(review.overall_rating)}`,
        comment ? 'Є коментар' : null,
        complaintLabels.length > 0 ? 'Є позначені скарги' : null,
    ]
        .filter((value): value is string => Boolean(value))
        .join(' • ');
    const toggleLabel = expanded ? 'Сховати відгук' : 'Показати відгук';
    const contentId = `order-review-summary-${review.id}`;

    return (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/90 p-4 dark:border-slate-700 dark:bg-slate-900/60">
            <button
                type="button"
                onClick={() => setExpanded((current) => !current)}
                aria-expanded={expanded}
                aria-controls={contentId}
                className="group flex w-full flex-col gap-2 rounded-xl text-left transition-colors outline-none hover:text-slate-950 focus-visible:ring-2 focus-visible:ring-primary/30 dark:hover:text-slate-100"
            >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        <MessageSquareText className="h-4 w-4 text-primary" />
                        {title}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                            <CalendarDays className="h-3.5 w-3.5" />
                            {formatDate(review.created_at)}
                        </div>
                        <div className="inline-flex items-center gap-1 font-medium text-slate-700 transition-colors group-hover:text-slate-950 dark:text-slate-300 dark:group-hover:text-slate-100">
                            {toggleLabel}
                            <ChevronDown
                                className={cn(
                                    'h-4 w-4 transition-transform duration-200',
                                    expanded && 'rotate-180',
                                )}
                            />
                        </div>
                    </div>
                </div>

                {!expanded && (
                    <p className="text-sm text-muted-foreground">
                        {collapsedSummary}
                    </p>
                )}
            </button>

            {expanded && (
                <div
                    id={contentId}
                    className="mt-4 space-y-3 border-t border-slate-200 pt-4 dark:border-slate-700"
                >
                    <div className="flex flex-wrap gap-2">
                        <Badge
                            variant={ratingBadgeVariant(review.overall_rating)}
                        >
                            <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                            Загалом:{' '}
                            {getOrderReviewRatingLabel(review.overall_rating)}
                        </Badge>
                        <Badge variant="outline">
                            <ScrollText className="mr-1 h-3.5 w-3.5" />
                            Опис:{' '}
                            {getOrderReviewRatingLabel(
                                review.item_description_rating,
                            )}
                        </Badge>
                        {hasPackagingRating && (
                            <Badge variant="outline">
                                <PackageCheck className="mr-1 h-3.5 w-3.5" />
                                Пакування:{' '}
                                {getOrderReviewRatingLabel(
                                    review.packaging_rating,
                                )}
                            </Badge>
                        )}
                    </div>

                    <div className="rounded-md bg-white/80 p-3 text-sm leading-6 dark:bg-slate-800/80 dark:text-slate-100">
                        {comment ? (
                            <p className="break-words whitespace-pre-wrap">
                                {comment}
                            </p>
                        ) : (
                            <span className="text-muted-foreground">
                                Текстовий коментар до цього відгуку не залишено.
                            </span>
                        )}
                    </div>

                    {complaintLabels.length > 0 && (
                        <div className="space-y-2">
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                Позначені скарги
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {complaintLabels.map((label) => (
                                    <Badge key={label} variant="neutral">
                                        {label}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
